import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../auth/supabase';
import './admin.css';
import './v12-admin.css';

function when(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function label(value) {
  return String(value || '').replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase());
}

export default function NotificationAudit({ profile }) {
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [feedPosts, setFeedPosts] = useState([]);
  const [burns, setBurns] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [channel, setChannel] = useState('all');

  const load = useCallback(async () => {
    if (!profile?.is_admin) return;
    setError('');
    const [eventResult, memberResult, feedResult, burnResult] = await Promise.all([
      supabase.from('notification_events').select('*').order('requested_at', { ascending: false }).limit(500),
      supabase.rpc('admin_list_members'),
      supabase.from('program_feed_posts').select('id,title'),
      supabase.from('prescribed_burns').select('id,name'),
    ]);
    const firstError = eventResult.error || memberResult.error || feedResult.error || burnResult.error;
    if (firstError) {
      setError(firstError.message || 'Unable to load notification audit.');
      setStatus('error');
      return;
    }
    setEvents(Array.isArray(eventResult.data) ? eventResult.data : []);
    setMembers(Array.isArray(memberResult.data) ? memberResult.data : []);
    setFeedPosts(Array.isArray(feedResult.data) ? feedResult.data : []);
    setBurns(Array.isArray(burnResult.data) ? burnResult.data : []);
    setStatus('ready');
  }, [profile]);

  useEffect(() => { load(); }, [load]);

  const memberName = id => members.find(member => member.id === id)?.full_name || members.find(member => member.id === id)?.email || 'Unknown member';
  const sourceName = item => {
    if (item.source_type === 'program_feed') return feedPosts.find(post => post.id === item.source_id)?.title || 'Program Feed post';
    if (item.source_type === 'prescribed_burn') return burns.find(burn => burn.id === item.source_id)?.name || 'Prescribed Burn';
    return label(item.source_type || 'system');
  };
  const sourceHref = item => {
    if (item.source_type === 'program_feed' && item.source_id) return `/program-feed/${item.source_id}`;
    if (item.source_type === 'prescribed_burn' && item.source_id) return `/prescribed-burns/${item.source_id}`;
    return '';
  };
  const filtered = useMemo(() => events.filter(item => (filter === 'all' || item.status === filter) && (channel === 'all' || item.channel === channel)), [events, filter, channel]);
  const counts = useMemo(() => ({
    queued: events.filter(item => item.status === 'queued').length,
    accepted: events.filter(item => item.status === 'provider_accepted').length,
    failed: events.filter(item => ['failed','invalid_token','no_token'].includes(item.status)).length,
    sms: events.filter(item => item.channel === 'sms').length,
  }), [events]);

  if (!profile?.is_admin) return <div className="admin-root"><main className="admin-page"><section className="admin-card admin-denied"><h1>Admin access required</h1></section></main></div>;

  return (
    <div className="admin-root">
      <header className="admin-topbar">
        <div><h1>Operational Audit</h1><p>APNs, optional SMS, and Gear Room access audit without overstating provider acceptance as delivery/read</p></div>
        <div className="admin-topbar-actions"><button type="button" onClick={() => window.location.assign('/admin/door-access')}>Door Access</button><button type="button" onClick={load}>Refresh</button><button type="button" onClick={() => window.location.assign('/admin')}>Admin Home</button></div>
      </header>
      <main className="admin-page">
        <div className="admin-summary-grid">
          <div className="admin-stat"><span>Queued</span><strong>{counts.queued}</strong></div>
          <div className="admin-stat"><span>Provider accepted</span><strong>{counts.accepted}</strong></div>
          <div className="admin-stat"><span>Needs attention</span><strong>{counts.failed}</strong></div>
          <div className="admin-stat"><span>SMS events</span><strong>{counts.sms}</strong></div>
        </div>
        <section className="admin-card">
          <div className="admin-section-head">
            <div><h2>Notification Events</h2><p>`Provider Accepted` means Apple/Twilio accepted the provider request; it is intentionally not labeled Delivered or Read.</p></div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label>Channel <select value={channel} onChange={event => setChannel(event.target.value)}><option value="all">All</option><option value="apns">APNs Push</option><option value="sms">SMS</option></select></label>
              <label>Status <select value={filter} onChange={event => setFilter(event.target.value)}><option value="all">All</option><option value="queued">Queued</option><option value="requested">Requested</option><option value="provider_accepted">Provider Accepted</option><option value="failed">Failed</option><option value="invalid_token">Invalid Token</option><option value="no_token">No Token</option><option value="opted_out">SMS Opted Out</option><option value="cancelled">Cancelled</option></select></label>
            </div>
          </div>
          {status === 'loading' && <div className="admin-empty">Loading notification history…</div>}
          {error && <div className="v12-error">{error}</div>}
          {filtered.map(item => {
            const href = sourceHref(item);
            return (
              <div className="v12-audit-grid" key={item.id}>
                <div><strong>{memberName(item.member_id)}</strong><small>{label(item.event_type)}</small></div>
                <div><span>{label(item.channel)} · {label(item.source_type)}</span><small>{href ? <button type="button" onClick={() => window.location.assign(href)} style={{ marginTop: 4 }}>{sourceName(item)} →</button> : sourceName(item)}</small></div>
                <span>{label(item.status)}{item.error_class ? ` · ${item.error_class}` : ''}</span>
                <span>{when(item.updated_at || item.requested_at)}{item.provider ? ` · ${label(item.provider)}` : ''}</span>
              </div>
            );
          })}
          {status === 'ready' && !filtered.length && <div className="admin-empty">No notification events match these filters.</div>}
        </section>
      </main>
    </div>
  );
}
